/**
 * Round-3 arkiv-sort client-side renderer.
 *
 * Activates only when the Liquid template emits #arkiv-sort-data with the
 * page-slice handle list — i.e. only on collections >250 products in arkiv
 * mode. Smaller collections keep using the round-2 server-side render.
 *
 * Contract from collection-template-dj.liquid:
 *
 *   <ul id="facets-results" class="collection-grid-dj ..."></ul>
 *
 *   <script id="arkiv-sort-data" type="application/json">
 *     {"handles": ["handle-a", "handle-b", ...], "gridSelector": "#facets-results"}
 *   </script>
 *
 * For each handle, fetch /products/<handle>?view=arkiv-card and inject the
 * returned card HTML in order. The arkiv-card template uses `layout none` so
 * the response is just the card markup + the snippet's inline <style>/<script>.
 *
 * Order is preserved because Promise.all returns results in input order
 * regardless of completion order. Failed fetches are logged and skipped so
 * one bad handle can't sink the whole grid.
 *
 * Re-runs on AJAX grid swaps: the theme's Alpine `filterFormSubmit` pipeline
 * swaps the collection grid section via AJAX (e.g. when the user picks a
 * new sort from the dropdown) — the IIFE only fires once on page load, so
 * we use a MutationObserver on the body to detect a new #arkiv-sort-data
 * element appearing and re-run init for it.
 */
(function () {
  'use strict';

  // Track which #arkiv-sort-data node we've already processed so we only
  // act on a NEW one (the AJAX swap replaces the node entirely).
  var processedDataEl = null;

  function stripCardForInjection(html, keepStyle) {
    // Each fetched card brings the v3 snippet's inline <style> and <script>
    // blocks. The styles need to apply on the page (bootstrap card carries
    // them on initial load, but an AJAX dropdown swap blows the bootstrap
    // card away — the only inline styles that survive are the ones we
    // keep on the FIRST injected card). The scripts are inert when
    // assigned via innerHTML, so we always strip them.
    //
    // keepStyle=true preserves <style> blocks (used for the first card
    // in a page slice); keepStyle=false strips them too (cards 2..N).
    var doc = new DOMParser().parseFromString(html, 'text/html');
    if (!keepStyle) {
      doc.querySelectorAll('style').forEach(function (el) { el.remove(); });
    }
    doc.querySelectorAll('script').forEach(function (el) { el.remove(); });
    return doc.body ? doc.body.innerHTML : '';
  }

  function renderGrid(data) {
    var handles = Array.isArray(data.handles) ? data.handles : [];
    var gridSelector = data.gridSelector || '#facets-results';
    var grid = document.querySelector(gridSelector);
    if (!grid) {
      console.warn('[arkiv-sort] grid container not found:', gridSelector);
      return;
    }
    if (!handles.length) return;

    grid.setAttribute('data-arkiv-loading', 'true');

    var fetches = handles.map(function (handle, index) {
      return fetch('/products/' + encodeURIComponent(handle) + '?view=arkiv-card', {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' },
      })
        .then(function (resp) {
          if (!resp.ok) {
            throw new Error('HTTP ' + resp.status + ' for handle=' + handle);
          }
          return resp.text();
        })
        .then(function (html) {
          return { handle: handle, html: html, ok: true, index: index };
        })
        .catch(function (err) {
          console.warn('[arkiv-sort] fetch failed for handle=' + handle, err);
          return { handle: handle, html: '', ok: false, index: index };
        });
    });

    Promise.all(fetches).then(function (results) {
      // Keep <style> on the first SUCCESSFUL card so the v3 CSS rules survive
      // an Alpine AJAX swap (which wipes #facets-filterable wholesale,
      // including the server-rendered bootstrap card's inline <style>).
      // Strip from cards 2..N to avoid 25× duplicated CSS in the DOM.
      var keptStyleYet = false;
      var combined = results
        .filter(function (r) { return r.ok; })
        .map(function (r) {
          var keepStyle = !keptStyleYet;
          if (keepStyle) keptStyleYet = true;
          return stripCardForInjection(r.html, keepStyle);
        })
        .join('');

      // Single DOM write — build the full HTML string and assign once. Avoids
      // layout thrashing from N inserts and keeps the page's reflow count low.
      grid.innerHTML = combined;
      grid.removeAttribute('data-arkiv-loading');

      // Re-fire dj-v3 cart-state sync for newly inserted cards.
      if (typeof window.__cardV3RefreshAllFromCart === 'function') {
        try { window.__cardV3RefreshAllFromCart(); } catch (e) { /* ignore */ }
      }
    });
  }

  /**
   * True when the page is showing a filtered or searched subset rather than the
   * whole collection.
   *
   * The baked order in #arkiv-sort-data is the ENTIRE collection, so painting it
   * over a filtered view leaves the count narrowed and the grid full - the bug
   * Dylan hit searching Bach inside Classical.
   *
   * This lives in JS rather than Liquid because in-collection search is served
   * by Cloud Search & Filter through a Shopify app proxy, and the Liquid guard
   * in collection-template-dj could not see that context: request.path and the
   * app's own parameter both came back empty there, so the data element is
   * emitted regardless. location is unambiguous.
   */
  function isFilteredView() {
    var path = window.location.pathname || '';
    var query = window.location.search || '';
    if (path.indexOf('/a/search') !== -1) return true;
    return /[?&](q|filter\.|filter_search)/.test(query);
  }

  function processCurrentDataEl() {
    // NOTE: this guard is deliberately NOT applied here.
    //
    // In client-render mode the server emits no products at all - the grid is
    // a bootstrap shell that this script fills. Bailing out therefore produced
    // an EMPTY grid on "sorted by Newest, then search Bach", which is worse
    // than the full-grid bug it was meant to fix.
    //
    // The correct place to stop this is server-side, by not entering
    // client-render mode on a filtered page at all. Kept here only so the
    // helper is available once that lands.

    var dataEl = document.getElementById('arkiv-sort-data');
    if (!dataEl) return;
    if (dataEl === processedDataEl) return;  // already handled this exact node
    processedDataEl = dataEl;

    var data;
    try {
      data = JSON.parse(dataEl.textContent || '{}');
    } catch (err) {
      console.error('[arkiv-sort] could not parse #arkiv-sort-data JSON', err);
      return;
    }
    renderGrid(data);
  }

  /**
   * Keep Cloud Search & Filter's search term alive across sorting and paging.
   *
   * In-collection search is served through an app proxy at
   * /a/search/<collection>?filter_search_collection=<term>. The theme's sort
   * control (theme.js filterFormSubmit) rebuilds the query string from the
   * form's own fields only, and the pagination links are built as
   * {{ request.path }}?sort_by=..., so neither carries a parameter the theme
   * does not know about. Sorting a search result therefore dropped the search
   * and showed the whole collection again.
   *
   * Shopify strips unknown parameters from request.params on a proxy page, so
   * the theme cannot re-emit this server-side. It has to be done here, from
   * location.search.
   */
  var FOREIGN_PARAMS = ['filter_search_collection'];

  function preserveForeignParams() {
    var current = new URLSearchParams(window.location.search);
    var carry = FOREIGN_PARAMS.filter(function (name) { return current.get(name); });
    if (!carry.length) return;

    // 1. Sort/filter forms: add a hidden field so FormData picks the value up.
    var selects = document.querySelectorAll('select[name="sort_by"]');
    Array.prototype.forEach.call(selects, function (sel) {
      var form = sel.form;
      if (!form) return;
      carry.forEach(function (name) {
        if (form.querySelector('[name="' + name + '"]')) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = current.get(name);
        form.appendChild(input);
      });
    });

    // 2. Pagination and sort links built from request.path.
    var links = document.querySelectorAll('a[href*="sort_by="]');
    Array.prototype.forEach.call(links, function (a) {
      var href;
      try { href = new URL(a.getAttribute('href'), window.location.origin); }
      catch (e) { return; }
      if (href.origin !== window.location.origin) return;
      var changed = false;
      carry.forEach(function (name) {
        if (!href.searchParams.get(name)) {
          href.searchParams.set(name, current.get(name));
          changed = true;
        }
      });
      if (changed) a.setAttribute('href', href.pathname + href.search);
    });
  }

  /**
   * Remove Arkiv Newest / Oldest from the sort dropdown on a filtered view.
   *
   * Those two sorts are served from a baked order that lists the WHOLE
   * collection, so they cannot describe a search result. Leaving them in the
   * dropdown offered a sort that silently did nothing.
   *
   * Reordering the rendered cards by date is NOT a substitute: the page holds
   * one slice of the results, so it would reorder ~50 cards out of several
   * thousand and present page 1 of a relevance-ordered set as "Newest". Wrong
   * in a way a shopper cannot see, which is worse than the option being absent.
   */
  function hideBakedSortOptions() {
    if (!isFilteredView()) return;
    var selects = document.querySelectorAll('select[name="sort_by"]');
    Array.prototype.forEach.call(selects, function (sel) {
      Array.prototype.slice.call(sel.options).forEach(function (opt) {
        if (opt.value && opt.value.indexOf('arkiv-') === 0) opt.remove();
      });
    });
  }

  function init() {
    preserveForeignParams();
    hideBakedSortOptions();

    // First run: handle whatever #arkiv-sort-data exists on initial DOM.
    processCurrentDataEl();

    // Watch for AJAX swaps that bring a new #arkiv-sort-data into the DOM.
    // The theme's Alpine filterFormSubmit pipeline replaces the grid section
    // wholesale on dropdown changes; the new HTML contains a fresh data
    // element which we need to act on.
    var observer = new MutationObserver(function (mutations) {
      // Only trigger a re-process when nodes are added — checking mutation
      // .addedNodes for ANY element is cheap, and processCurrentDataEl
      // bails out fast if the data element hasn't changed.
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length > 0) {
          // The Alpine pipeline replaces the whole grid section on a sort or
          // filter change, bringing a fresh form and fresh pagination links -
          // so the carried parameters have to be re-applied to them.
          preserveForeignParams();
          hideBakedSortOptions();
          processCurrentDataEl();
          return;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
